import React, { useCallback, useEffect } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { Link } from '~/lib/react-router-dom';

import {
  BusinessCenter,
  Cancel,
  CheckCircle,
  ContentCopy,
  LocalOffer,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  GridLegacy as Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

import DefinitionText from '~/components/Common/Elements/DefinitionText';
import DefinitionTitle from '~/components/Common/Elements/DefinitionTitle';

import {
  useGetRoomContentSources,
  useUpdateRoomContentSources,
} from '~/hooks/apis/accommodation/useRoomContentSources';

import { RoomResponse, UpdateRoomContentSourcesPayload, patchRoom } from '~/services/AccommodationService';

import ContentSourceFieldRow, {
  getActiveSource,
  getSelectableSources,
} from '../AccommodationPropertyContentSources/ContentSourceFieldRow';
import useContentSourceFieldStates from '../AccommodationPropertyContentSources/useContentSourceFieldStates';

interface Props {
  roomData: RoomResponse;
  propertyId: string;
  onRoomUpdated?: (room: RoomResponse) => void;
}

interface RoomNameFormData {
  name: string;
}

export default function AccommodationPropertyRoomViewDetail({ roomData, propertyId, onRoomUpdated }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const numberOfBathrooms = roomData.numberOfBathrooms ?? null;
  const roomViews = roomData.views ?? [];
  const isRoomVisible = roomData.visibility === 'auto_enabled' || roomData.visibility === 'force_enabled';

  const { control, handleSubmit, watch, reset } = useForm<RoomNameFormData>({
    defaultValues: {
      name: roomData.name || '',
    },
  });

  const currentName = watch('name');
  const hasChanges = currentName !== roomData.name;

  useEffect(() => {
    reset({
      name: roomData.name || '',
    });
  }, [roomData.name, reset]);

  const updateRoomMutation = useMutation({
    mutationFn: async (data: RoomNameFormData) => {
      return await patchRoom({ propertyId, roomId: roomData.id }, data);
    },
    onSuccess: (response) => {
      const updatedRoom = response.result;
      enqueueSnackbar('Room name updated successfully', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['rooms', propertyId] });
      reset({ name: updatedRoom.name });
      if (onRoomUpdated) {
        onRoomUpdated(updatedRoom);
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating room name: ${error.message}`, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    (data: RoomNameFormData) => {
      updateRoomMutation.mutate(data);
    },
    [updateRoomMutation],
  );

  const {
    data: contentSourcesData,
    isLoading: isContentSourcesLoading,
    error: contentSourcesError,
  } = useGetRoomContentSources({ roomId: roomData.id });
  const updateContentSourcesMutation = useUpdateRoomContentSources();
  const {
    getChangedFields,
    getFieldState,
    handleFieldChange,
    hasChanges: hasContentSourceChanges,
  } = useContentSourceFieldStates(contentSourcesData?.fields, contentSourcesData?.availableSources);

  const handleSaveContentSources = useCallback(() => {
    if (!contentSourcesData) {
      return;
    }

    const changedFields = getChangedFields() as UpdateRoomContentSourcesPayload['fields'];
    if (changedFields.length === 0) {
      return;
    }

    updateContentSourcesMutation.mutate(
      { roomId: roomData.id, data: { fields: changedFields } },
      {
        onSuccess: () => {
          enqueueSnackbar('Room content sources updated', { variant: 'success' });
        },
        onError: (err: Error) => {
          enqueueSnackbar(`Failed to update: ${err.message}`, { variant: 'error' });
        },
      },
    );
  }, [contentSourcesData, getChangedFields, roomData.id, updateContentSourcesMutation, enqueueSnackbar]);

  const handleCopyClick = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
  };

  const getStatusChipColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'content-approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Room content source editing is currently scoped to the main description field.
  const descriptionFieldSelection = contentSourcesData?.fields.find((f) => f.field === 'description');

  return (
    <Stack direction="column" spacing={3}>
      {/* Room Header */}
      <Card>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <DefinitionTitle>Name</DefinitionTitle>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          size="small"
                          fullWidth
                          disabled={updateRoomMutation.isPending}
                          sx={{ flexGrow: 1 }}
                        />
                      )}
                    />
                    <Chip
                      icon={roomData.hasActiveLpcPackage ? <CheckCircle /> : <Cancel />}
                      label={roomData.hasActiveLpcPackage ? 'Active LPC Package' : 'No Active LPC Package'}
                      color={roomData.hasActiveLpcPackage ? 'success' : 'error'}
                      variant="outlined"
                    />
                    {roomData.visibility !== undefined && (
                      <Tooltip title="When hidden, the room will not be in content and no rates will be shown unless all rates = true">
                        <Chip
                          icon={isRoomVisible ? <Visibility /> : <VisibilityOff />}
                          label={isRoomVisible ? 'Visible' : 'Hidden'}
                          color={isRoomVisible ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!hasChanges || updateRoomMutation.isPending}
                      size="small"
                    >
                      {updateRoomMutation.isPending ? 'Saving...' : 'Save'}
                    </Button>
                  </Stack>
                </form>
              </Box>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DefinitionTitle>Room ID</DefinitionTitle>
                <DefinitionText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {roomData.id}
                    </Typography>
                    <Tooltip title="Copy Room ID">
                      <IconButton size="small" onClick={() => handleCopyClick(roomData.id, 'Room ID')}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </DefinitionText>
              </Grid>

              <Grid item xs={12} md={6}>
                <DefinitionTitle>Suppliers</DefinitionTitle>
                <DefinitionText>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {roomData.suppliers.map((supplier) => (
                      <Chip key={supplier} label={supplier} size="small" variant="outlined" color="primary" />
                    ))}
                  </Stack>
                </DefinitionText>
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DefinitionTitle>Number of bedrooms</DefinitionTitle>
                <DefinitionText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {roomData.numberOfBedrooms}
                    </Typography>
                  </Box>
                </DefinitionText>
              </Grid>

              <Grid item xs={12} md={6}>
                <DefinitionTitle>Size</DefinitionTitle>
                <DefinitionText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {roomData.sizeSqm ? `${roomData.sizeSqm}m²` : '—'}
                    </Typography>
                  </Box>
                </DefinitionText>
              </Grid>

              <Grid item xs={12} md={6}>
                <DefinitionTitle>Number of bathrooms</DefinitionTitle>
                <DefinitionText>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {numberOfBathrooms ?? '—'}
                    </Typography>
                  </Box>
                </DefinitionText>
              </Grid>

              <Grid item xs={12} md={6}>
                <DefinitionTitle>Views</DefinitionTitle>
                <DefinitionText>
                  {roomViews.length ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {roomViews.map((view, index) => (
                        <Chip key={`${view}-${index}`} label={view} size="small" variant="outlined" color="primary" />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Not specified
                    </Typography>
                  )}
                </DefinitionText>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      {/* Content */}
      {(isContentSourcesLoading || descriptionFieldSelection) && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Box>
                <Typography variant="h6">Content</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select the source override.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fields left empty have the best source selected when serving the content.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSaveContentSources}
                disabled={updateContentSourcesMutation.isPending || !hasContentSourceChanges}
              >
                Save
              </Button>
            </Stack>

            {contentSourcesError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                Error loading content sources:{' '}
                {contentSourcesError instanceof Error ? contentSourcesError.message : 'Unknown error'}
              </Alert>
            )}

            {isContentSourcesLoading && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}

            {descriptionFieldSelection && contentSourcesData && (
              <ContentSourceFieldRow
                fieldSelection={descriptionFieldSelection}
                activeSource={getActiveSource(descriptionFieldSelection, contentSourcesData.availableSources)}
                availableSources={getSelectableSources(descriptionFieldSelection, contentSourcesData.availableSources)}
                fieldState={getFieldState(descriptionFieldSelection)}
                onChange={handleFieldChange}
                isPending={updateContentSourcesMutation.isPending}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Offer Packages */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <BusinessCenter />
            <Typography variant="h6">Offer Packages ({roomData.offerPackages.length})</Typography>
          </Stack>

          {roomData.offerPackages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No offer packages available for this room.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {roomData.offerPackages.map((offerPackage, index) => (
                <Accordion key={offerPackage.id} defaultExpanded={index === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <LocalOffer color="primary" />
                      <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                        {offerPackage.name}
                      </Typography>
                      <Chip
                        label={formatStatus(offerPackage.status)}
                        color={getStatusChipColor(offerPackage.status)}
                        size="small"
                      />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <Stack spacing={2}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>LE Package ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.lePackageId}
                              </Typography>
                              <Tooltip title="Copy LE Package ID">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyClick(offerPackage.lePackageId, 'LE Package ID')}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Salesforce External ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.idSalesforceExternal}
                              </Typography>
                              <Tooltip title="Copy Salesforce External ID">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyClick(offerPackage.idSalesforceExternal, 'Salesforce External ID')
                                  }
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>
                      </Grid>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Reservation Room ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.reservationRoomId}
                              </Typography>
                              <Tooltip title="Copy Reservation Room ID">
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyClick(offerPackage.reservationRoomId, 'Reservation Room ID')}
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <DefinitionTitle>Reservation Property ID</DefinitionTitle>
                          <DefinitionText>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {offerPackage.reservationPropertyId}
                              </Typography>
                              <Tooltip title="Copy Reservation Property ID">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleCopyClick(offerPackage.reservationPropertyId, 'Reservation Property ID')
                                  }
                                >
                                  <ContentCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </DefinitionText>
                        </Grid>
                      </Grid>

                      <Divider />

                      <Box>
                        <DefinitionTitle>Package Options</DefinitionTitle>
                        <DefinitionText>
                          {offerPackage.packageOptions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No package options available
                            </Typography>
                          ) : (
                            <Box>
                              {offerPackage.packageOptions.map((option) => (
                                <Stack
                                  direction="row"
                                  gap={2}
                                  spacing={2}
                                  key={option.roomRateId}
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={1}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {option.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ fontFamily: 'monospace' }}
                                    >
                                      Rate ID: {option.roomRateId}
                                    </Typography>
                                    <Tooltip title="Copy Rate ID">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleCopyClick(option.roomRateId, 'Rate ID')}
                                      >
                                        <ContentCopy fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Link
                                    to={`/vendors/unknown/properties/${offerPackage.reservationPropertyId}/room-types/${offerPackage.reservationRoomId}/room-rates/${option.roomRateId}`}
                                    target="_blank"
                                  >
                                    <Button variant="contained" color="primary">
                                      View Rate
                                    </Button>
                                  </Link>
                                </Stack>
                              ))}
                            </Box>
                          )}
                        </DefinitionText>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
